import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Presentation, Upload, Trash2, Loader2, FileText, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ───────────────────────── Public button + dialog ─────────────────────────
   Single component used at the top of every department dialog (Workspace,
   TechnicalPlans, PropertyBudgets). Passes a stable `dept_id` so each
   department has its own bucket of presentations. Admins see upload/delete
   controls; viewers just see "Open" links that launch the fullscreen PDF
   viewer in-place. */
export default function PresentationsButton({ deptId, deptLabel }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  // Light-touch count fetch so the button can show a badge.
  const refreshCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/presentations/${deptId}`);
      setCount(Array.isArray(data) ? data.length : 0);
    } catch { /* ignore */ }
  }, [deptId]);
  useEffect(() => { refreshCount(); }, [refreshCount]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all hover:-translate-y-0.5"
        style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope', boxShadow: '0 2px 8px rgba(0,61,165,0.25)' }}
        data-testid={`presentations-btn-${deptId}`}
        title="Open presentations"
      >
        <Presentation className="w-3.5 h-3.5" />
        Presentations
        {count > 0 && (
          <span className="ml-0.5 px-1.5 rounded-full text-[9px]" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            {count}
          </span>
        )}
      </button>
      <PresentationsDialog
        open={open}
        onClose={() => setOpen(false)}
        deptId={deptId}
        deptLabel={deptLabel}
        onCountChange={setCount}
      />
    </>
  );
}

function PresentationsDialog({ open, onClose, deptId, deptLabel, onCountChange }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null); // { url, title }

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/presentations/${deptId}`);
      setItems(data || []);
      onCountChange?.(data?.length || 0);
    } catch (e) {
      toast.error('Could not load presentations');
    } finally {
      setLoading(false);
    }
  }, [open, deptId, onCountChange]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => /\.pdf$/i.test(f.name));
    if (!files.length) {
      if (e.target.files?.length) toast.error('Only PDF files are supported');
      return;
    }
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('dept_id', deptId);
      fd.append('title', f.name.replace(/\.pdf$/i, ''));
      fd.append('file', f);
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/presentations`, fd, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        ok += 1;
      } catch (err) {
        console.error('Presentation upload failed for', f.name, err);
      }
    }
    toast.success(`Uploaded ${ok}/${files.length} presentation${ok === 1 ? '' : 's'}`);
    e.target.value = '';
    fetchItems();
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this presentation?')) return;
    try {
      await axios.delete(`${API}/admin/presentations/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true,
      });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[88vh] overflow-y-auto p-0" style={{ backgroundColor: '#F4F1EB' }}>
          <div className="p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                  <Presentation className="w-3 h-3 inline mr-1 -mt-0.5" /> Presentations
                </p>
                <h2 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E', fontWeight: 500 }}>
                  {deptLabel || deptId}
                </h2>
              </div>
              {adminToken && (
                <div className="flex items-center gap-2">
                  <label
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white cursor-pointer transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
                    data-testid={`presentations-upload-${deptId}`}
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? 'Uploading…' : 'Upload PDF'}
                    <input type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={upload} disabled={uploading} />
                  </label>
                  <button
                    type="button"
                    data-testid={`presentations-ai-enhance-${deptId}`}
                    onClick={async () => {
                      const { getAiProviderArgs } = await import('../lib/aiProviderPicker');
                      const topic = window.prompt(`Enhance your ${deptLabel || deptId} presentation deck with AI. What's the topic / angle?`, '');
                      if (!topic) return;
                      const audienceChoice = window.prompt('Audience? ownership | staff | external', 'ownership');
                      if (!audienceChoice) return;
                      const providerArgs = await getAiProviderArgs();
                      if (providerArgs === null) return;
                      toast.message('Drafting your deck — this can take 20-30s.', { duration: 5000 });
                      try {
                        const { data } = await axios.post(`${API}/admin/ai/polish-document`, {
                          raw_text: `Department: ${deptLabel || deptId}\nTopic / focus: ${topic}\n\nGenerate a board-ready presentation outline (Title slide → Exec Summary → 4-6 content sections → Next Steps → Q&A prompt). Include speaker notes per slide as italicised text under each H2 section.`,
                          doc_type: 'report',
                          audience: audienceChoice,
                          keep_original: false,
                          ...providerArgs,
                        }, { headers: { Authorization: `Bearer ${adminToken}` } });
                        const w = window.open('', '_blank');
                        if (w) {
                          w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${(data.title || 'AI Deck').replace(/[<>]/g, '')}</title><style>body{font-family:Manrope,system-ui;max-width:780px;margin:36px auto;padding:0 24px;color:#0A1A2F;line-height:1.6}h1{color:#003DA5;border-bottom:2px solid #003DA5;padding-bottom:8px}h2{color:#1F4FBE;margin-top:32px}blockquote{border-left:4px solid #003DA5;padding-left:16px;color:#4A5D5D;font-style:italic;background:#F8F5EC;padding:12px 16px;border-radius:4px}pre{white-space:pre-wrap;background:#F8F5EC;padding:18px;border-radius:8px;font-family:ui-monospace,Menlo,monospace;font-size:13px;line-height:1.55}</style></head><body>`);
                          w.document.write(`<pre>${(data.polished_markdown || '').replace(/[<>&]/g, (m) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[m]))}</pre>`);
                          w.document.write('</body></html>');
                          w.document.close();
                        }
                        toast.success('AI deck draft opened in a new tab.');
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'AI enhance failed.');
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white cursor-pointer transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 12px rgba(139,92,246,0.35)' }}
                    title="Generate a polished presentation outline with AI"
                  >
                    ✨ Enhance with AI
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="py-16 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#003DA5' }} /></div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center">
                <Presentation className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: '#003DA5' }} />
                <p className="text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No presentations uploaded yet.
                  {adminToken && <><br />Upload a PDF to get started.</>}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-xl p-4 flex items-center gap-3 transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}
                    data-testid={`presentation-row-${p.id}`}
                  >
                    <div
                      className="w-12 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#F0F4FA' }}
                    >
                      <FileText className="w-6 h-6" style={{ color: '#003DA5' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                        {p.title}
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                        {(p.size_bytes / 1024 / 1024).toFixed(2)} MB · PDF
                      </p>
                    </div>
                    <button
                      onClick={() => setViewing({ url: p.url.startsWith('http') ? p.url : `${BACKEND}${p.url}`, title: p.title })}
                      className="px-3 py-1.5 rounded-full text-[10px] font-bold text-white"
                      style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                      data-testid={`presentation-open-${p.id}`}
                    >
                      Open
                    </button>
                    {adminToken && (
                      <button
                        onClick={() => remove(p.id)}
                        className="p-1.5 rounded-full hover:bg-red-50"
                        style={{ color: '#DC2626' }}
                        title="Delete"
                        data-testid={`presentation-delete-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen PDF viewer — opens on top of the list dialog so users can
          flip through several presentations without losing the list state. */}
      {viewing && (
        <PdfViewer url={viewing.url} title={viewing.title} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

function PdfViewer({ url, title, onClose }) {
  // Native browser PDF rendering inside an iframe — works in all modern
  // browsers including iOS Safari (which downloads on tap rather than rendering
  // inline; we offer an explicit "Open in new tab" fallback for that case).
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[98vw] max-w-7xl h-[92vh] p-0" style={{ backgroundColor: '#1A2E2E' }} data-testid="pdf-viewer">
        <div className="flex items-center justify-between px-4 py-2.5" style={{ backgroundColor: '#0E1F2C' }}>
          <p className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Manrope' }}>{title}</p>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
              data-testid="pdf-viewer-open-tab"
            >
              <ExternalLink className="w-3 h-3" /> Open in new tab
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
              data-testid="pdf-viewer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <iframe
          src={`${url}#toolbar=1&view=FitH`}
          title={title}
          className="w-full"
          style={{ height: 'calc(92vh - 50px)', border: 0, backgroundColor: '#1A2E2E' }}
        />
      </DialogContent>
    </Dialog>
  );
}
